import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button, Icon, Loading, Modal, Spinner, Tooltip, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator, useProfile } from "@/hooks";
import { useGetVehicleFormOptionsQuery, useReactivateVehicleMutation, useVerifyVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, formatCurrency, getCountryFlagSrc } from "@/utils";
import { VehicleVerificationErrorAlert } from "../../AddVehicle/common/VehicleVerificationErrorAlert";
import { getVerificationErrorType } from "../../AddVehicle/helpers";

interface UnarchiveVehicleModalProps {
  vehicle?: Vehicle;
  onClose: () => void;
}

export const UnarchiveVehicleModal = ({ vehicle, onClose }: UnarchiveVehicleModalProps) => {
  const { id: operatorId } = useOperator();
  const [step, setStep] = useState<"confirm" | "details">("confirm");

  const handleClose = () => {
    setStep("confirm");
    onClose();
  };

  if (!vehicle) return null;

  const isOperatorOwned = vehicle.owner.uuid === operatorId;

  if (!isOperatorOwned) {
    return <DriverOwnedConfirmStep vehicle={vehicle} onClose={handleClose} />;
  }

  if (step === "confirm") {
    return <ConfirmStep vehicle={vehicle} onClose={handleClose} onContinue={() => setStep("details")} />;
  }

  return <DetailsStep vehicle={vehicle} onClose={handleClose} />;
};

const DriverOwnedConfirmStep = ({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [reactivateVehicle, { isLoading }] = useReactivateVehicleMutation();

  const handleUnarchive = async () => {
    try {
      await reactivateVehicle(vehicle.uuid).unwrap();
      addToast("info", `Vehicle ${vehicle.registrationNumber} has been added back to My Vehicles tab`);
      dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      onClose();
      if (pathname.includes("/archived-vehicles/")) {
        navigate("../", { replace: true });
      }
    } catch (e) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    }
  };

  return (
    <Modal open onClose={onClose} className="max-w-[400px]">
      <div className="relative">
        {isLoading && <Loading />}
        <Typography variant="title">Unarchive Vehicle</Typography>
        <Typography variant="paragraph" className="mt-3 text-neutral-dark-gray">
          Are you sure you want to unarchive <span className="text-info">{vehicle.registrationNumber}</span>? This will move the vehicle back
          to the My Driver&apos;s Vehicles tab. The assigned driver will need to reconfirm the vehicle registration in the Driver App before
          it can be used again.
        </Typography>
        <div className="mt-6 flex justify-end gap-4">
          <Button variant="secondary" onClick={onClose} className="px-8">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUnarchive} disabled={isLoading}>
            Unarchive Vehicle
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const ConfirmStep = ({ vehicle, onClose, onContinue }: { vehicle: Vehicle; onClose: () => void; onContinue: () => void }) => {
  const { data } = useGetVehicleFormOptionsQuery();
  const fee = data?.fees.verificationFee ?? 0;

  return (
    <Modal open onClose={onClose} className="max-w-[400px]">
      <Typography variant="title">Unarchive Vehicle</Typography>
      <Typography variant="paragraph" className="mt-3 text-neutral-dark-gray">
        Are you sure you want to unarchive <span className="text-info">{vehicle.registrationNumber}</span>? This will bring it back to the
        My Vehicles tab, and you&apos;ll need to reconfirm its registration for {formatCurrency(fee, 2, "AUD")}.
      </Typography>
      <div className="mt-6 flex justify-end gap-4">
        <Button variant="secondary" onClick={onClose} className="px-8">
          Cancel
        </Button>
        <Button variant="primary" onClick={onContinue}>
          Unarchive Vehicle
        </Button>
      </div>
    </Modal>
  );
};

const DetailsStep = ({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { dateFormat } = useProfile();
  const { data } = useGetVehicleFormOptionsQuery();
  const [verifyVehicle, { error: verifyError }] = useVerifyVehicleMutation();
  const [reactivateVehicle] = useReactivateVehicleMutation();
  const [isProcessing, setIsProcessing] = useState(false);

  const fee = data?.fees.verificationFee ?? 0;
  const flagSrc = getCountryFlagSrc(vehicle.country.countryCode);

  const errorType = useMemo(() => {
    if (!verifyError) return undefined;
    return getVerificationErrorType(verifyError);
  }, [verifyError]);

  const handleReconfirm = async () => {
    setIsProcessing(true);
    try {
      await verifyVehicle(vehicle.uuid).unwrap();
      await reactivateVehicle(vehicle.uuid).unwrap();
      addToast("info", `Vehicle ${vehicle.registrationNumber} has been added back to My Vehicles tab`);
      dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      onClose();
      if (pathname.includes("/archived-vehicles/")) {
        navigate("../", { replace: true });
      }
    } catch (e) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal open onClose={onClose} className="!max-w-[550px] !p-6">
      <div className="relative">
        {isProcessing && <Loading />}

        <Typography variant="title">Unarchive and Reconfirm Vehicle Registration</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          To bring this vehicle back to your active list, please review the details below and reconfirm its registration
        </Typography>

        {errorType && <VehicleVerificationErrorAlert errorType={errorType} />}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <DetailRow label="Make" value={vehicle.model.make} />
          <DetailRow label="Model" value={vehicle.model.model} />
          <DetailRow label="Plate Number" value={vehicle.registrationNumber} />
          <DetailRow label="Year" value={vehicle.year?.toString() ?? "-"} />
          <DetailRow
            label="Color"
            value={
              vehicle.color ? (
                <span className="inline-flex items-center gap-2 capitalize">
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                  />
                  {vehicle.color.toLowerCase().replace("_", " ")}
                </span>
              ) : (
                "-"
              )
            }
          />
          <DetailRow label="Engine Type" value={vehicle.model.engine ? vehicle.model.engine.toLowerCase() : "-"} />
          <DetailRow label="Passenger Capacity" value={`${vehicle.capacity.pax} pax`} />
          <DetailRow label="Bag Capacity" value={vehicle.capacity.bags.toString()} />
          <DetailRow
            label="State Registered"
            value={
              <span className="inline-flex items-center gap-1">
                {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
                {vehicle.state.abbreviation}
              </span>
            }
          />
          <DetailRow
            label="Registration Expiry"
            value={vehicle.registration.expiryDate ? format(new Date(vehicle.registration.expiryDate), dateFormat) : "-"}
          />
          <DetailRow label="VIN" value={vehicle.vin ?? "-"} />
          <DetailRow
            label={
              <span className="inline-flex items-center gap-1">
                Vehicle Category
                <Tooltip content="System-defined category based on vehicle make/model" placement="top">
                  <Icon name="InfoCircle" variant="Bold" className="text-neutral-dark-gray" size="sm" />
                </Tooltip>
              </span>
            }
            value={vehicle.vehicleCategory?.name ?? "-"}
          />
          <DetailRow
            label={
              <span className="inline-flex items-center gap-1">
                Vehicle Type
                <Tooltip content="Operator-defined vehicle type used within your own fleet setup" placement="top">
                  <Icon name="InfoCircle" variant="Bold" className="text-neutral-dark-gray" size="sm" />
                </Tooltip>
              </span>
            }
            value={vehicle.vehicleType?.name ?? "-"}
          />
          <DetailRow label="Vehicle Nickname" value={vehicle.identifier || "-"} />
          <DetailRow label="Vehicle has markings (Signage/Decals)?" value={vehicle.hasMarkings ? "Yes" : "No"} />
          <DetailRow label="Vehicle Owner Maintenance Fee" value={vehicle.maintenanceFee ? `${vehicle.maintenanceFee}%` : "-"} />
          <DetailRow label="Access Level" value={renderAccessLevel(vehicle)} />
          <DetailRow label="Insurance Document" value={renderDocument(vehicle, "INSURANCE")} />
          <DetailRow label="Accreditation Document" value={renderDocument(vehicle, "ACCREDITATION")} />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleReconfirm} disabled={isProcessing}>
            {isProcessing ? <Spinner /> : `Reconfirm Registration for ${formatCurrency(fee, 2, "AUD")}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const renderAccessLevel = (vehicle: Vehicle) => {
  const type = vehicle.accessType;
  if (type === "ALL") return "All Drivers";
  if (type === "TRANSFER") return "Transfer Only";
  if (type === "RESTRICTED") return `Restricted (${vehicle.driverCount ?? 0} Drivers)`;
  return "-";
};

const renderDocument = (vehicle: Vehicle, type: "INSURANCE" | "ACCREDITATION") => {
  const doc = vehicle.documents.find((d) => d.documentType === type);
  if (!doc) return "-";

  return (
    <span className="inline-flex items-center gap-1.5 truncate">
      <Icon name="file-pdf" isCustom size="sm" className="shrink-0" />
      <span className="truncate">{doc.filename}</span>
    </span>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
